import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import {
  getPlaidHoldings,
  getPlaidTransactions,
  getPlaidWealthSnapshot,
  getAccountDetailData,
  TimeRange,
} from '@/api/custom';
import { resolveFirmDataEnabled } from '@/hooks/useFirmDataEnabled';
import { resolveCurrentWealthAccounts } from '@wavvest/shared-utils';

const loadCurrentWealthSnapshot = async (
  accountIds: string[] | undefined,
  range: TimeRange,
  firmDataEnabled: boolean,
) =>
  resolveCurrentWealthAccounts(
    await getPlaidWealthSnapshot(accountIds, range, firmDataEnabled),
    async (accountId) => (await getAccountDetailData(accountId)).account_detail,
    (visibleIds) => getPlaidWealthSnapshot(visibleIds, range, firmDataEnabled),
  );

export const PLAID_STALE_USER_REQUEST = 'stale-user-request';

type ActiveUserIdentity = {
  userId: string | null;
  cognitoId: string | null;
};

const normalizeId = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const getActiveUserIdFromState = (state: RootState): ActiveUserIdentity => ({
  userId: normalizeId(state.userinfo.user?.userId),
  cognitoId: normalizeId(state.userinfo.user?.cognito_id),
});

// The wealth snapshot read routes to wavvest-client-portal-backend's own
// /wealth/snapshot endpoint for firm-data-enabled investors, and to
// plaid-cash-integration directly otherwise. Resolved here (rather than
// threaded in from callers) so every dispatch site gets the correct routing
// for free. This is a general firm-data-enabled signal, distinct from the
// Apex-specific apex_correspondent_id/apex_enabled gate used elsewhere.
const getFirmDataEnabledFromState = (state: RootState): Promise<boolean> =>
  resolveFirmDataEnabled(state.userinfo.user?.client_id);

const identitiesOverlap = (first: ActiveUserIdentity, second: ActiveUserIdentity): boolean => {
  const firstIds = [first.cognitoId, first.userId].filter((id): id is string => Boolean(id));
  const secondIds = [second.cognitoId, second.userId].filter((id): id is string => Boolean(id));

  if (firstIds.length === 0 || secondIds.length === 0) {
    return false;
  }

  return firstIds.some((id) => secondIds.includes(id));
};

const identityMatchesRequestedId = (identity: ActiveUserIdentity, requestedId: string): boolean => {
  const normalizedRequestedId = normalizeId(requestedId);
  if (!normalizedRequestedId) {
    return false;
  }

  return identity.userId === normalizedRequestedId || identity.cognitoId === normalizedRequestedId;
};

// Compatibility thunk: now sourced from wealth snapshot instead of legacy endpoint.
export const fetchPlaidAccounts = createAsyncThunk(
  'plaid/fetchAccounts',
  async (_: void, { getState, rejectWithValue }) => {
    const startingIdentity = getActiveUserIdFromState(getState() as RootState);

    try {
      const firmDataEnabled = await getFirmDataEnabledFromState(getState() as RootState);
      const response = await loadCurrentWealthSnapshot(undefined, 'ALL', firmDataEnabled);

      if (!identitiesOverlap(startingIdentity, getActiveUserIdFromState(getState() as RootState))) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }

      return {
        accounts: response.accounts || [],
        summary: {
          total_assets: response.summary?.total_assets || 0,
          total_liabilities: response.summary?.total_liabilities || 0,
          net_worth: response.summary?.net_worth || 0,
        },
      };
    } catch (error) {
      if (!identitiesOverlap(startingIdentity, getActiveUserIdFromState(getState() as RootState))) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }
      throw error;
    }
  },
);

export const fetchPlaidHoldings = createAsyncThunk(
  'plaid/fetchHoldings',
  async ({ activeUserId }: { activeUserId: string }, { getState, rejectWithValue }) => {
    const startingIdentity = getActiveUserIdFromState(getState() as RootState);
    if (!identityMatchesRequestedId(startingIdentity, activeUserId)) {
      return rejectWithValue(PLAID_STALE_USER_REQUEST);
    }

    try {
      const response = await getPlaidHoldings();

      if (
        !identityMatchesRequestedId(getActiveUserIdFromState(getState() as RootState), activeUserId)
      ) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }

      return { holdings: response, activeUserId };
    } catch (error) {
      if (
        !identityMatchesRequestedId(getActiveUserIdFromState(getState() as RootState), activeUserId)
      ) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }
      throw error;
    }
  },
);

export const fetchPlaidTransactions = createAsyncThunk(
  'plaid/fetchTransactions',
  async ({ activeUserId }: { activeUserId: string }, { getState, rejectWithValue }) => {
    const startingIdentity = getActiveUserIdFromState(getState() as RootState);
    if (!identityMatchesRequestedId(startingIdentity, activeUserId)) {
      return rejectWithValue(PLAID_STALE_USER_REQUEST);
    }

    try {
      const response = await getPlaidTransactions();

      if (
        !identityMatchesRequestedId(getActiveUserIdFromState(getState() as RootState), activeUserId)
      ) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }

      return { transactions: response, activeUserId };
    } catch (error) {
      if (
        !identityMatchesRequestedId(getActiveUserIdFromState(getState() as RootState), activeUserId)
      ) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }
      throw error;
    }
  },
);

// Compatibility thunk: now sourced from wealth snapshot instead of legacy chart endpoint.
export const fetchPlaidChartData = createAsyncThunk(
  'plaid/fetchChartData',
  async (
    {
      accountIds,
      range = 'ALL',
      activeUserId,
    }: {
      accountIds?: string[];
      range?: TimeRange;
      activeUserId: string;
    },
    { getState, rejectWithValue },
  ) => {
    const startingIdentity = getActiveUserIdFromState(getState() as RootState);
    if (!identityMatchesRequestedId(startingIdentity, activeUserId)) {
      return rejectWithValue(PLAID_STALE_USER_REQUEST);
    }

    try {
      const firmDataEnabled = await getFirmDataEnabledFromState(getState() as RootState);
      const response = await getPlaidWealthSnapshot(accountIds, range, firmDataEnabled);

      if (
        !identityMatchesRequestedId(getActiveUserIdFromState(getState() as RootState), activeUserId)
      ) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }

      return { chartData: response.chart, activeUserId };
    } catch (error) {
      if (
        !identityMatchesRequestedId(getActiveUserIdFromState(getState() as RootState), activeUserId)
      ) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }
      throw error;
    }
  },
);

export const fetchPlaidWealthSnapshot = createAsyncThunk(
  'plaid/fetchWealthSnapshot',
  async (
    {
      accountIds,
      range = 'ALL',
    }: {
      accountIds?: string[];
      range?: TimeRange;
    },
    { getState, rejectWithValue },
  ) => {
    const startingIdentity = getActiveUserIdFromState(getState() as RootState);

    try {
      const firmDataEnabled = await getFirmDataEnabledFromState(getState() as RootState);
      const response = await loadCurrentWealthSnapshot(accountIds, range, firmDataEnabled);

      if (!identitiesOverlap(startingIdentity, getActiveUserIdFromState(getState() as RootState))) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }

      return response;
    } catch (error) {
      if (!identitiesOverlap(startingIdentity, getActiveUserIdFromState(getState() as RootState))) {
        return rejectWithValue(PLAID_STALE_USER_REQUEST);
      }
      throw error;
    }
  },
);
